import { CalendarDays, Check, Pencil, Plus, Trash2 } from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "./Feedback";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate } from "../utils/format";
import DateField from "./DateField";

type NationalHoliday = {
  id: number;
  code: string;
  date: string;
  name: string;
  is_optional: boolean;
  enabled: boolean;
};

type CustomHoliday = {
  id: number;
  date: string;
  name: string;
  annual_recurrence: boolean;
};

type HolidaySettingsResponse = {
  year: number;
  scope: "personal" | "organization";
  can_manage: boolean;
  national_holidays: NationalHoliday[];
  custom_holidays: CustomHoliday[];
};

type HolidayDraft = {
  id: number | null;
  name: string;
  date: string;
  annual_recurrence: boolean;
};

export default function HolidaySettings({
  organizationId,
  canManage,
}: {
  organizationId: number | null;
  canManage: boolean;
}) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [settings, setSettings] = useState<HolidaySettingsResponse | null>(null);
  const [draft, setDraft] = useState<HolidayDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const query = `?year=${year}${organizationId ? `&organization=${organizationId}` : ""}`;
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<HolidaySettingsResponse>(
        `/core/holidays/settings/${query}`,
      );
      setSettings(response.data);
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De feestdagen konden niet worden geladen."));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveSelections = async (nationalHolidays: NationalHoliday[]) => {
    setWorking(true);
    setError("");
    try {
      const response = await api.put<HolidaySettingsResponse>(
        `/core/holidays/settings/${query}`,
        {
          selections: nationalHolidays.map(({ code, enabled }) => ({ code, enabled })),
        },
      );
      setSettings(response.data);
      setSuccess("Feestdageninstellingen opgeslagen.");
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De feestdageninstellingen konden niet worden opgeslagen."));
    } finally {
      setWorking(false);
    }
  };

  const toggleNationalHoliday = (code: string, enabled: boolean) => {
    if (!settings || !canManage || working) return;
    const nationalHolidays = settings.national_holidays.map((holiday) =>
      holiday.code === code ? { ...holiday, enabled } : holiday,
    );
    setSettings({ ...settings, national_holidays: nationalHolidays });
    void saveSelections(nationalHolidays);
  };

  const saveCustomHoliday = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft || !canManage) return;
    setWorking(true);
    setError("");
    try {
      if (draft.id) {
        await api.patch(`/core/holidays/${draft.id}/`, draft);
      } else {
        await api.post("/core/holidays/", {
          ...draft,
          ...(organizationId ? { organization: organizationId } : {}),
        });
      }
      setDraft(null);
      setSuccess("Feestdag opgeslagen.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De feestdag kon niet worden opgeslagen."));
    } finally {
      setWorking(false);
    }
  };

  const removeCustomHoliday = async (holiday: CustomHoliday) => {
    if (!canManage || !window.confirm(`${holiday.name} verwijderen?`)) return;
    setWorking(true);
    try {
      await api.delete(`/core/holidays/${holiday.id}/`);
      setSuccess("Feestdag verwijderd.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De feestdag kon niet worden verwijderd."));
    } finally {
      setWorking(false);
    }
  };

  if (loading) return <LoadingState label="Feestdagen laden..." />;
  if (!settings) return error ? <ErrorNotice>{error}</ErrorNotice> : null;

  return (
    <>
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      <section className="surface settings-section">
        <div className="settings-heading">
          <div>
            <h2>Feestdagen</h2>
            <p>
              {organizationId
                ? "Kies de nationale feestdagen die binnen deze CAO als betaalde vrije dag gelden."
                : "Kies welke nationale feestdagen voor jouw persoonlijke verlofkaart gelden."}
            </p>
          </div>
          <CalendarDays size={22} />
        </div>
        {!canManage && (
          <div className="notice notice-info compact">
            Alleen een organisatiebeheerder kan de CAO-feestdagen wijzigen.
          </div>
        )}
        <label className="year-picker">
          <span>Kalenderjaar</span>
          <select value={year} onChange={(event) => setYear(Number(event.target.value))}>
            {[year - 1, year, year + 1, year + 2].map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </label>
        <div className="calendar-settings-list holiday-settings-list">
          {settings.national_holidays.map((holiday) => (
            <label className="toggle-field" key={holiday.code}>
              <input
                type="checkbox"
                checked={holiday.enabled}
                disabled={!canManage || working}
                onChange={(event) => toggleNationalHoliday(holiday.code, event.target.checked)}
              />
              <span>
                <strong>{holiday.name}</strong>
                <small>{formatDate(holiday.date)}{holiday.is_optional ? " · Niet standaard in iedere CAO" : ""}</small>
              </span>
            </label>
          ))}
        </div>
      </section>

      <section className="surface settings-section">
        <div className="settings-heading">
          <div>
            <h2>Eigen feestdagen</h2>
            <p>Voeg een werkgeversdag of bijzondere vrije dag toe.</p>
          </div>
          {canManage && (
            <button
              className="button primary"
              type="button"
              onClick={() => setDraft({ id: null, name: "", date: `${year}-01-01`, annual_recurrence: false })}
            >
              <Plus size={17} /> Toevoegen
            </button>
          )}
        </div>
        <div className="calendar-settings-list">
          {settings.custom_holidays.length === 0 ? (
            <p className="empty-copy">Nog geen eigen feestdagen toegevoegd.</p>
          ) : settings.custom_holidays.map((holiday) => (
            <article key={holiday.id}>
              <span>
                <strong>{holiday.name}</strong>
                <small>{formatDate(holiday.date)}{holiday.annual_recurrence ? " · Jaarlijks" : ""}</small>
              </span>
              {canManage && (
                <span className="row-actions">
                  <button
                    className="icon-button"
                    type="button"
                    title="Wijzigen"
                    aria-label={`${holiday.name} wijzigen`}
                    onClick={() => setDraft(holiday)}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="icon-button danger"
                    type="button"
                    title="Verwijderen"
                    aria-label={`${holiday.name} verwijderen`}
                    onClick={() => void removeCustomHoliday(holiday)}
                  >
                    <Trash2 size={16} />
                  </button>
                </span>
              )}
            </article>
          ))}
        </div>
      </section>

      {draft && (
        <div className="modal-backdrop" role="presentation">
          <form className="modal-panel settings-section" onSubmit={saveCustomHoliday}>
            <div className="settings-heading">
              <h2>{draft.id ? "Feestdag wijzigen" : "Feestdag toevoegen"}</h2>
            </div>
            <label>
              <span>Naam</span>
              <input
                value={draft.name}
                maxLength={120}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                required
              />
            </label>
            <label>
              <span>Datum</span>
              <DateField
                value={draft.date}
                onChange={(value) => setDraft({ ...draft, date: value })}
                required
              />
            </label>
            <label className="toggle-field">
              <input
                type="checkbox"
                aria-label="Jaarlijks herhalen"
                checked={draft.annual_recurrence}
                onChange={(event) => setDraft({ ...draft, annual_recurrence: event.target.checked })}
              />
              <span>
                <strong>Jaarlijks herhalen</strong>
                <small>Gebruik dezelfde maand en dag in volgende kalenderjaren.</small>
              </span>
            </label>
            <div className="form-actions">
              <button className="button subtle" type="button" onClick={() => setDraft(null)}>Annuleren</button>
              <button className="button primary" type="submit" disabled={working}>
                <Check size={17} /> Opslaan
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
